import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { Client } from '../../client/classes/client';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { BetriebAuswaehlenDialogComponent } from '../../dialogs/betrieb-auswaehlen-dialog/betrieb-auswaehlen-dialog.component';
import { Gruppe, isGruppeSichtbarFuerAusstallenUmstallen } from '../classes/gruppe';
import { GruppeAuswaehlenDialogInput, GruppeAuswaehlenDialogResult } from './gruppe-auswaehlen-dialog';

@Component({
    selector: 'kiene-gruppe-auswaehlen-dialog',
    templateUrl: './gruppe-auswaehlen-dialog.component.html',
    styleUrls: ['./gruppe-auswaehlen-dialog.component.scss'],
})
export class GruppeAuswaehlenDialogComponent implements OnInit, OnDestroy {
    gruppen: Gruppe[] = null;
    isLoading = true;
    ladeGruppenSub: Subscription;
    selectedVVVO: Client;
    searchQuery = '';

    constructor(
        public dialogRef: MatDialogRef<GruppeAuswaehlenDialogComponent, GruppeAuswaehlenDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: GruppeAuswaehlenDialogInput,
        private readonly dialog: MatDialog,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private stammdatenService: StammdatenService,
    ) {}

    ngOnDestroy(): void {
        this.ladeGruppenSub?.unsubscribe();
    }

    ngOnInit(): void {
        this.ladeGruppen(this.data?.kunde_id, this.data?.except_gruppe_id);
    }

    ladeGruppen(kunde_id: number, except_gruppe_id?: number) {
        this.isLoading = true;
        let params = new HttpParams();

        if (kunde_id) {
            params = params.set('kunde_id', kunde_id?.toString());
            if (this.stammdatenService?.getAusgewaehlteVvvo()?.kunde_id === kunde_id) {
                this.selectedVVVO = this.stammdatenService.getAusgewaehlteVvvo();
            }
        } else {
            this.selectedVVVO = this.stammdatenService.getAusgewaehlteVvvo();
            if (this.selectedVVVO) {
                params = params.set('kunde_id', this.selectedVVVO?.kunde_id?.toString());
            }
        }

        this.ladeGruppenSub?.unsubscribe();
        if (except_gruppe_id) {
            params = params.set('except_gruppe_id', except_gruppe_id.toString());
        }
        this.ladeGruppenSub = this.api.getAll(this.apiBaseUrl + 'gruppe/read_akt_kundenportal.php', params).subscribe({
            next: response => {
                this.isLoading = false;
                this.gruppen = (response.records || []).filter((gruppe: Gruppe) =>
                    isGruppeSichtbarFuerAusstallenUmstallen(gruppe),
                );
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Laden der Gruppen!', err);
            },
        });
    }

    get filteredGruppen(): Gruppe[] {
        const query = this.searchQuery?.trim().toLowerCase();
        if (!query) {
            return this.gruppen ?? [];
        }

        return (this.gruppen ?? []).filter(g => {
            const bezeichnung = g?.bezeichnung?.toLowerCase() ?? '';
            const stall = g?.stall?.toLowerCase() ?? '';
            const abteil = g?.abteil?.toLowerCase() ?? '';
            const kunde = g?.kunde?.toLowerCase() ?? '';
            const zusatz = g?.zusatz?.toLowerCase() ?? '';
            const anzahlTiere = String(g?.anzahl_tiere_aktuell ?? '');
            return (
                bezeichnung.includes(query) ||
                stall.includes(query) ||
                abteil.includes(query) ||
                kunde.includes(query) ||
                zusatz.includes(query) ||
                anzahlTiere.includes(query)
            );
        });
    }

    getGruppeMeta(gruppe: Gruppe): string {
        const parts: string[] = [];
        if (gruppe?.stall) {
            parts.push(`Stall: ${gruppe.stall}`);
        }
        if (gruppe?.abteil) {
            parts.push(`Abteil: ${gruppe.abteil}`);
        }
        return parts.join(' · ');
    }

    hinzufuegen(g: Gruppe) {
        const result = new GruppeAuswaehlenDialogResult();
        result.confirmed = true;
        result.gruppe = g;
        this.dialogRef.close(result);
    }

    mehrereGruppen() {
        if (!this.selectedVVVO) {
            this.dialog
                .open(BetriebAuswaehlenDialogComponent, {
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    width: '600px',
                    disableClose: true,
                })
                .afterClosed()
                .subscribe({
                    next: (betrieb: Client) => {
                        if (betrieb) {
                            this.stammdatenService.setAusgewaehlteVvvo(betrieb);
                            this.selectedVVVO = betrieb;
                            this.schliesseMehrereGruppen(betrieb);
                        } else {
                            this.messageService.infoMessage('Bitte wählen Sie einen Betrieb aus.');
                        }
                    },
                });
            return;
        }
        this.schliesseMehrereGruppen(this.selectedVVVO);
    }

    private schliesseMehrereGruppen(betrieb?: Client) {
        const result = new GruppeAuswaehlenDialogResult();
        result.confirmed = true;
        result.mehrereGruppen = true;
        result.ausstallenderBetrieb = betrieb;
        this.dialogRef.close(result);
    }

    close() {
        this.dialogRef.close();
    }
}
